/**
 * Reverse Relationship Generator
 * 
 * Automatically generates reverse relationship configurations by analyzing
 * Fulcrum forms for record link fields and creating bidirectional relationship
 * mappings for the CultureMap system.
 */

import { fetchFulcrumForm, fetchFulcrumForms } from '../fulcrumLibService';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface RecordLinkField {
  fieldId: string;
  fieldLabel: string;
  fieldType: string;
  targetFormId?: string;
  targetFormName?: string;
}

export interface FormRelationshipAnalysis {
  formId: string;
  formName: string;
  recordLinkFields: RecordLinkField[];
  outgoingRelationships: RelationshipMapping[];
}

export interface RelationshipMapping {
  sourceFormId: string;
  sourceFormName: string;
  targetFormId: string;
  targetFormName: string;
  recordLinkFieldId: string;
  recordLinkFieldLabel: string;
  relationshipKey: string;
  displayName: string;
}

export interface ReverseRelationshipConfig {
  sourceFormId: string;
  sourceFormName: string;
  recordLinkFieldId: string;
  recordLinkFieldLabel: string;
  relationshipKey: string;
  displayName: string;
}

export interface RelationshipGenerationOptions {
  userProfile?: any;
  includeAllForms?: boolean;
  autoDetectRelationshipKeys?: boolean;
  customRelationshipMappings?: Record<string, string>;
}

// ============================================================================
// RELATIONSHIP KEY GENERATION
// ============================================================================

/**
 * Generate a relationship key from form name
 */
const generateRelationshipKey = (formName: string): string => {
  return formName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
    .replace(/_+/g, '_'); // Collapse multiple underscores
};

/**
 * Generate display name from form name and field label
 */
const generateDisplayName = (formName: string, fieldLabel: string): string => {
  // If field label already contains form name, use as-is
  if (fieldLabel.toLowerCase().includes(formName.toLowerCase())) {
    return fieldLabel;
  }
  
  // Otherwise, combine them intelligently
  return `${formName} (${fieldLabel})`;
};

// ============================================================================
// FORM ANALYSIS FUNCTIONS
// ============================================================================

/**
 * Extract record link fields from form elements
 */
const extractRecordLinkFields = (elements: any[]): RecordLinkField[] => {
  const recordLinkFields: RecordLinkField[] = [];
  
  const walkElements = (elements: any[]) => {
    if (!Array.isArray(elements)) return;
    
    for (const element of elements) {
      if (element.type === 'record_link_field' && element.key) {
        recordLinkFields.push({
          fieldId: element.key,
          fieldLabel: element.label || element.data_name || element.key,
          fieldType: element.type,
          targetFormId: element.form_id, // Fulcrum record link fields specify target form
          targetFormName: undefined // Will be resolved later
        });
      }
      
      // Recursively process nested elements
      if (element.elements && Array.isArray(element.elements)) {
        walkElements(element.elements);
      }
    }
  };
  
  walkElements(elements);
  return recordLinkFields;
};

/**
 * Analyze a single form for record link relationships
 */
export const analyzeFormRelationships = async (
  formId: string,
  userProfile: any,
  allForms?: any[]
): Promise<FormRelationshipAnalysis> => {
  try {
    console.log(`[ReverseRelationshipGenerator] Analyzing relationships for form ${formId}`);
    
    // Fetch the form schema
    const formResponse = await fetchFulcrumForm(formId, userProfile);
    const form = formResponse.form || formResponse;
    
    if (!form || !form.elements) {
      throw new Error(`Invalid form schema for ${formId}`);
    }
    
    // Extract record link fields
    const recordLinkFields = extractRecordLinkFields(form.elements);
    
    console.log(`[ReverseRelationshipGenerator] Found ${recordLinkFields.length} record link fields in ${form.name}`);
    
    // Resolve target form names if we have the forms list
    if (allForms && Array.isArray(allForms)) {
      for (const field of recordLinkFields) {
        if (field.targetFormId) {
          const targetForm = allForms.find(f => f.id === field.targetFormId);
          if (targetForm) {
            field.targetFormName = targetForm.name;
          }
        }
      }
    }
    
    // Generate outgoing relationship mappings
    const outgoingRelationships: RelationshipMapping[] = recordLinkFields
      .filter(field => field.targetFormId && field.targetFormName)
      .map(field => ({
        sourceFormId: formId,
        sourceFormName: form.name,
        targetFormId: field.targetFormId!,
        targetFormName: field.targetFormName!,
        recordLinkFieldId: field.fieldId,
        recordLinkFieldLabel: field.fieldLabel,
        relationshipKey: generateRelationshipKey(field.targetFormName!),
        displayName: generateDisplayName(field.targetFormName!, field.fieldLabel)
      }));
    
    return {
      formId,
      formName: form.name,
      recordLinkFields,
      outgoingRelationships
    };
    
  } catch (error) {
    console.error(`[ReverseRelationshipGenerator] Error analyzing form ${formId}:`, error);
    throw error;
  }
};

/**
 * Analyze all forms to build complete relationship graph
 */
export const analyzeAllFormRelationships = async (
  userProfile: any,
  options: RelationshipGenerationOptions = {}
): Promise<FormRelationshipAnalysis[]> => {
  try {
    console.log('[ReverseRelationshipGenerator] Analyzing all form relationships');
    
    // Fetch all available forms
    const formsResponse = await fetchFulcrumForms(userProfile);
    const forms = formsResponse.forms || formsResponse;
    
    if (!forms || !Array.isArray(forms)) {
      throw new Error('No forms found or invalid response from Fulcrum API');
    }
    
    console.log(`[ReverseRelationshipGenerator] Found ${forms.length} forms to analyze`);
    
    // Analyze each form
    const analyses: FormRelationshipAnalysis[] = [];
    
    for (const form of forms) {
      try {
        const analysis = await analyzeFormRelationships(form.id, userProfile, forms);
        analyses.push(analysis);
      } catch (error) {
        console.warn(`[ReverseRelationshipGenerator] Failed to analyze form ${form.id}:`, error);
        // Continue with other forms
      }
    }
    
    console.log(`[ReverseRelationshipGenerator] Successfully analyzed ${analyses.length}/${forms.length} forms`);
    return analyses;
    
  } catch (error) {
    console.error('[ReverseRelationshipGenerator] Error analyzing form relationships:', error);
    throw error;
  }
};

// ============================================================================
// REVERSE RELATIONSHIP GENERATION
// ============================================================================

/**
 * Generate reverse relationships from relationship analysis
 */
export const generateReverseRelationships = (
  analyses: FormRelationshipAnalysis[]
): Record<string, ReverseRelationshipConfig[]> => {
  const reverseRelationships: Record<string, ReverseRelationshipConfig[]> = {};
  
  // Build reverse relationship map
  for (const analysis of analyses) {
    for (const relationship of analysis.outgoingRelationships) {
      const targetFormId = relationship.targetFormId;
      
      if (!reverseRelationships[targetFormId]) {
        reverseRelationships[targetFormId] = [];
      }
      
      reverseRelationships[targetFormId].push({
        sourceFormId: relationship.sourceFormId,
        sourceFormName: relationship.sourceFormName,
        recordLinkFieldId: relationship.recordLinkFieldId,
        recordLinkFieldLabel: relationship.recordLinkFieldLabel,
        relationshipKey: generateRelationshipKey(relationship.sourceFormName),
        displayName: generateDisplayName(relationship.sourceFormName, relationship.recordLinkFieldLabel)
      });
    }
  }
  
  return reverseRelationships;
};

/**
 * Generate form-to-relationship mapping
 */
export const generateFormToRelationshipMapping = (
  analyses: FormRelationshipAnalysis[]
): Record<string, string> => {
  const mapping: Record<string, string> = {};
  
  for (const analysis of analyses) {
    mapping[analysis.formId] = generateRelationshipKey(analysis.formName);
  }
  
  return mapping;
};

/**
 * Generate complete relationship configuration for config.json
 */
export const generateRelationshipConfiguration = async (
  userProfile: any,
  options: RelationshipGenerationOptions = {}
): Promise<any> => {
  try {
    console.log('[ReverseRelationshipGenerator] Generating complete relationship configuration');
    
    // Analyze all form relationships
    const analyses = await analyzeAllFormRelationships(userProfile, options);
    
    // Generate reverse relationships
    const reverseRelationships = generateReverseRelationships(analyses);
    
    // Generate form mapping
    const formToRelationshipMapping = generateFormToRelationshipMapping(analyses);
    
    // Build configuration structure
    const config = {
      formToRelationshipMapping,
      reverseRelationships,
      relationshipAnalysis: analyses, // Include for debugging/review
      generatedAt: new Date().toISOString(),
      totalForms: analyses.length,
      totalRelationships: analyses.reduce((sum, a) => sum + a.outgoingRelationships.length, 0)
    };
    
    console.log(`[ReverseRelationshipGenerator] Generated relationship configuration:`, {
      totalForms: config.totalForms,
      totalRelationships: config.totalRelationships,
      formsWithReverseRelationships: Object.keys(reverseRelationships).length
    });
    
    return config;
    
  } catch (error) {
    console.error('[ReverseRelationshipGenerator] Error generating relationship configuration:', error);
    throw error;
  }
};

/**
 * Generate reverse relationship configuration compatible with existing AdminComponent
 */
export const generateAdminCompatibleConfig = async (
  userProfile: any,
  options: RelationshipGenerationOptions = {}
): Promise<string> => {
  try {
    const config = await generateRelationshipConfiguration(userProfile, options);
    
    // Format for AdminComponent consumption
    const adminConfig = {
      // Form to relationship mapping for adapter pattern
      formToRelationshipMapping: config.formToRelationshipMapping,
      
      // Reverse relationships for each endpoint
      reverseRelationships: config.reverseRelationships,
      
      // Additional metadata
      _generated: {
        timestamp: config.generatedAt,
        totalForms: config.totalForms,
        totalRelationships: config.totalRelationships,
        generator: 'ReverseRelationshipGenerator v1.0'
      }
    };
    
    return JSON.stringify(adminConfig, null, 2);
    
  } catch (error) {
    console.error('[ReverseRelationshipGenerator] Error generating admin-compatible config:', error);
    throw error;
  }
};

// ============================================================================
// VALIDATION & TESTING
// ============================================================================

/**
 * Validate generated relationship configuration
 */
export const validateRelationshipConfiguration = (config: any): boolean => {
  try {
    // Check required properties
    if (!config.formToRelationshipMapping || typeof config.formToRelationshipMapping !== 'object') {
      console.error('[ReverseRelationshipGenerator] Invalid formToRelationshipMapping');
      return false;
    }
    
    if (!config.reverseRelationships || typeof config.reverseRelationships !== 'object') {
      console.error('[ReverseRelationshipGenerator] Invalid reverseRelationships');
      return false;
    }
    
    // Validate reverse relationship structure
    for (const [formId, relationships] of Object.entries(config.reverseRelationships)) {
      if (!Array.isArray(relationships)) {
        console.error(`[ReverseRelationshipGenerator] Invalid relationships array for form ${formId}`);
        return false;
      }
      
      for (const rel of relationships as any[]) {
        if (!rel.sourceFormId || !rel.recordLinkFieldId || !rel.relationshipKey) {
          console.error(`[ReverseRelationshipGenerator] Invalid relationship structure:`, rel);
          return false;
        }
      }
    }
    
    console.log('[ReverseRelationshipGenerator] Configuration validation passed');
    return true;
    
  } catch (error) {
    console.error('[ReverseRelationshipGenerator] Validation error:', error);
    return false;
  }
};

// ============================================================================
// EXPORT UTILITIES
// ============================================================================

export const reverseRelationshipGenerator = {
  analyzeFormRelationships,
  analyzeAllFormRelationships,
  generateReverseRelationships,
  generateFormToRelationshipMapping,
  generateRelationshipConfiguration,
  generateAdminCompatibleConfig,
  validateRelationshipConfiguration
};